import { useEffect, useState } from 'react';
import { useAuth, API_BASE } from '../lib/auth';

interface DebugData {
  authMe?: { status: number; body: unknown; corsOrigin: string | null };
  dashboardStats?: { status: number; body: unknown };
  fetchError?: string;
}

export default function Debug() {
  const { state, refresh } = useAuth();
  const [data, setData] = useState<DebugData>({});
  const [now, setNow] = useState(Date.now());

  const runProbes = async () => {
    setNow(Date.now());
    try {
      const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
      const meBody = await meRes.json().catch(() => 'parse-error');
      const statsRes = await fetch(`${API_BASE}/api/pro/dashboard-stats`, { credentials: 'include' });
      const statsBody = await statsRes.json().catch(() => 'parse-error');
      setData({
        authMe: {
          status: meRes.status,
          body: meBody,
          corsOrigin: meRes.headers.get('access-control-allow-origin'),
        },
        dashboardStats: {
          status: statsRes.status,
          body: statsBody,
        },
      });
    } catch (err) {
      setData({ fetchError: err instanceof Error ? err.message : String(err) });
    }
  };

  useEffect(() => {
    runProbes();
  }, []);

  const cookieAvailable = typeof document !== 'undefined' && document.cookie.length > 0;
  const cookies = typeof document !== 'undefined' ? document.cookie.split(';').map(c => c.trim().split('=')[0]) : [];

  const wrap: React.CSSProperties = {
    fontFamily: 'var(--mono)',
    fontSize: 12,
    padding: 24,
    background: '#0f1410',
    color: '#d9f0df',
    minHeight: '100vh',
    lineHeight: 1.55,
  };
  const h2: React.CSSProperties = {
    fontFamily: 'var(--sans)',
    color: '#ff7a1a',
    margin: '16px 0 6px',
    fontSize: 14,
  };
  const code: React.CSSProperties = {
    display: 'block',
    background: 'rgba(255,255,255,0.06)',
    padding: 12,
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    fontFamily: 'var(--mono)',
    fontSize: 11,
  };
  const btn: React.CSSProperties = {
    padding: '8px 14px',
    background: '#0b8a3e',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'var(--sans)',
    fontSize: 12,
    marginRight: 8,
  };

  return (
    <div style={wrap}>
      <h1 style={{ fontFamily: 'var(--sans)', color: '#fff', fontSize: 20, marginTop: 0 }}>
        Pro Web · Debug
      </h1>
      <p>Snapshot live di auth + CORS + cookie. Refresh: {new Date(now).toLocaleTimeString('it-IT')}</p>

      <div style={{ margin: '12px 0 20px' }}>
        <button style={btn} onClick={runProbes}>Re-run probes</button>
        <button style={btn} onClick={refresh}>Refresh AuthContext</button>
      </div>

      <h2 style={h2}>Environment</h2>
      <pre style={code}>
{JSON.stringify(
  {
    origin: window.location.origin,
    href: window.location.href,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    user_agent: navigator.userAgent.slice(0, 100),
    API_BASE,
    VITE_API_BASE_env: import.meta.env.VITE_API_BASE,
  },
  null,
  2,
)}
      </pre>

      <h2 style={h2}>Cookies visibili (non-HttpOnly)</h2>
      <pre style={code}>
{JSON.stringify(
  {
    document_cookie_available: cookieAvailable,
    cookie_names: cookies,
    NOTA: 'Il session cookie è HttpOnly → NON appare qui. Lo invia il browser automaticamente con credentials:include.',
  },
  null,
  2,
)}
      </pre>

      <h2 style={h2}>AuthContext state</h2>
      <pre style={code}>{JSON.stringify(state, null, 2)}</pre>

      <h2 style={h2}>Probe /api/auth/me (credentials: include)</h2>
      <pre style={code}>{JSON.stringify(data.authMe, null, 2)}</pre>

      <h2 style={h2}>Probe /api/pro/dashboard-stats (credentials: include)</h2>
      <pre style={code}>{JSON.stringify(data.dashboardStats, null, 2)}</pre>

      {data.fetchError && (
        <>
          <h2 style={{ ...h2, color: '#ef4444' }}>Fetch error</h2>
          <pre style={code}>{data.fetchError}</pre>
        </>
      )}

      <h2 style={h2}>Cosa significa</h2>
      <pre style={code}>
{`SUCCESSO (loggato cross-subdomain):
  - AuthContext state = { status: 'authenticated', user: {...} }
  - /api/auth/me status 200 + body.user popolato + access-control-allow-origin = ${window.location.origin}
  - /api/pro/dashboard-stats status 200 + body con totali

FALLIMENTO COMUNE:
  - /api/auth/me 200 ma body.user = null  →  cookie NON arriva al backend
       Cause: cookie SameSite block, cookie Domain non matcha, terze-parti bloccate dal browser
  - /api/auth/me 401  →  endpoint protetto (NON dovrebbe — è pubblico, segnalare bug)
  - /api/pro/dashboard-stats 401  →  sei loggato ma role != dietologist OPPURE cookie non passa
  - Fetch error CORS  →  Origin non in allowlist server-side (verifica backend)
  - state.status = 'error'  →  network o CORS preflight fallito
`}
      </pre>
    </div>
  );
}
